import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { QuillModule } from 'ngx-quill';
import { TabNotesService, TabNote } from '../../services/tab-notes.service';

// Define all available tabs
const AVAILABLE_TABS = [
  { id: 'overview', name: 'Overview' },
  { id: 'ai-citations', name: 'AI Citations' },
  { id: 'category-analysis', name: 'Category Analysis' },
  { id: 'sentiment-tracking', name: 'Sentiment Tracking' },
  { id: 'consumer-insights', name: 'Consumer Insights' },
  { id: 'ai-content-index', name: 'AI Content Index' },
  { id: 'content-creation', name: 'Content Creation' },
  { id: 'performance-tracking', name: 'Performance Tracking' },
  { id: 'eeat-audit', name: 'EEAT Audit' },
  { id: 'landing-page-audit', name: 'Landing Page Audit' },
  { id: 'ai-audit', name: 'AI Audit' },
  { id: 'prompt-research', name: 'Prompt Research' },
  { id: 'prompt-tracking', name: 'Prompt Tracking' }
];

@Component({
  selector: 'app-tab-notes-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatCardModule,
    QuillModule
  ],
  templateUrl: './tab-notes-management.component.html',
  styleUrl: './tab-notes-management.component.scss'
})
export class TabNotesManagementComponent implements OnInit {
  private tabNotesService = inject(TabNotesService);
  private snackBar = inject(MatSnackBar);

  tabs = AVAILABLE_TABS;
  tabNotes: Map<string, TabNote> = new Map();
  editingContent: Map<string, string> = new Map();
  loading = false;
  saving: Map<string, boolean> = new Map();

  quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'font': [] }],
      [{ 'align': [] }],
      ['clean'],
      ['link', 'image']
    ]
  };

  ngOnInit(): void {
    this.loadAllTabNotes();
  }

  loadAllTabNotes(): void {
    this.loading = true;
    this.tabNotesService.getAllTabNotes().subscribe({
      next: (response) => {
        // Map existing notes by tab_name
        response.notes.forEach(note => {
          this.tabNotes.set(note.tab_name, note);
          this.editingContent.set(note.tab_name, note.content);
        });
        
        // Initialize editing content for tabs without notes
        this.tabs.forEach(tab => {
          if (!this.editingContent.has(tab.id)) {
            this.editingContent.set(tab.id, '');
          }
        });
        
        this.loading = false;
      },
      error: (err) => {
        console.error('Failed to load tab notes:', err);
        this.snackBar.open(
          err.error?.detail || err.error?.message || 'Failed to load tab notes',
          'Close',
          { duration: 5000 }
        );
        this.loading = false;
        
        // Initialize editing content even on error
        this.tabs.forEach(tab => {
          if (!this.editingContent.has(tab.id)) {
            this.editingContent.set(tab.id, '');
          }
        });
      }
    });
  }

  getTabNote(tabId: string): TabNote | undefined {
    return this.tabNotes.get(tabId);
  }

  getEditingContent(tabId: string): string {
    return this.editingContent.get(tabId) || '';
  }

  onContentChange(tabId: string, content: string): void {
    this.editingContent.set(tabId, content);
  }

  saveTabNote(tabId: string): void {
    const content = this.editingContent.get(tabId) || '';
    const existingNote = this.tabNotes.get(tabId);
    
    this.saving.set(tabId, true);

    if (existingNote) {
      // Update existing note
      this.tabNotesService.updateTabNote(tabId, { content }).subscribe({
        next: (updatedNote) => {
          this.tabNotes.set(tabId, updatedNote);
          this.snackBar.open(`Note for "${this.getTabName(tabId)}" saved successfully`, 'Close', { duration: 3000 });
          this.saving.set(tabId, false);
        },
        error: (err) => {
          console.error(`Failed to update tab note ${tabId}:`, err);
          this.snackBar.open(
            err.error?.detail || err.error?.message || 'Failed to save note',
            'Close',
            { duration: 5000 }
          );
          this.saving.set(tabId, false);
        }
      });
    } else {
      // Create new note
      this.tabNotesService.createTabNote({ tab_name: tabId, content }).subscribe({
        next: (newNote) => {
          this.tabNotes.set(tabId, newNote);
          this.snackBar.open(`Note for "${this.getTabName(tabId)}" created successfully`, 'Close', { duration: 3000 });
          this.saving.set(tabId, false);
        },
        error: (err) => {
          console.error(`Failed to create tab note ${tabId}:`, err);
          this.snackBar.open(
            err.error?.detail || err.error?.message || 'Failed to save note',
            'Close',
            { duration: 5000 }
          );
          this.saving.set(tabId, false);
        }
      });
    }
  }

  deleteTabNote(tabId: string): void {
    if (!confirm(`Are you sure you want to delete the note for "${this.getTabName(tabId)}"?`)) {
      return;
    }

    this.tabNotesService.deleteTabNote(tabId).subscribe({
      next: () => {
        this.tabNotes.delete(tabId);
        this.editingContent.set(tabId, '');
        this.snackBar.open(`Note for "${this.getTabName(tabId)}" deleted successfully`, 'Close', { duration: 3000 });
      },
      error: (err) => {
        console.error(`Failed to delete tab note ${tabId}:`, err);
        this.snackBar.open(
          err.error?.detail || err.error?.message || 'Failed to delete note',
          'Close',
          { duration: 5000 }
        );
      }
    });
  }

  getTabName(tabId: string): string {
    const tab = this.tabs.find(t => t.id === tabId);
    return tab ? tab.name : tabId;
  }

  isSaving(tabId: string): boolean {
    return this.saving.get(tabId) || false;
  }

  hasNote(tabId: string): boolean {
    return this.tabNotes.has(tabId);
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  }
}
